import { getSiteConfig } from '@shared/config';
import type { FieldPath, UseFormReturn } from 'react-hook-form';
import { z, type ZodSafeParseResult } from 'zod';
import { createRegistrationAddressSchema } from '../../form-controls/Address/schema';
import type { PlayerProfileAddressDataPatch } from './types';

function createAddressEditFormSchema(t: (key: string) => string) {
  return z.object({
    address: createRegistrationAddressSchema(t),
    addressLookup: z.string().optional(),
  });
}

type AddressEditFormData = z.infer<ReturnType<typeof createAddressEditFormSchema>>;

function applyAddressSchemaValidationErrors(
  form: Pick<UseFormReturn<AddressEditFormData>, 'setError' | 'clearErrors'>,
  parseResult: ZodSafeParseResult<AddressEditFormData['address']>
): boolean {
  form.clearErrors('address');

  if (parseResult.success) {
    return true;
  }

  for (const issue of parseResult.error.issues) {
    const fieldKey = issue.path[0];
    if (typeof fieldKey !== 'string') {
      continue;
    }

    form.setError(`address.${fieldKey}` as FieldPath<AddressEditFormData>, {
      type: 'manual',
      message: issue.message,
    });
  }

  return false;
}

function buildProfileAddressDataPayload(address: AddressEditFormData['address']): PlayerProfileAddressDataPatch {
  const postCode = address.postcode.toUpperCase().replace(/\s/g, '');
  const addressLine1 = [address.houseNameNumber, address.streetName].filter(Boolean).join(' ');
  const showsStateSelect = getSiteConfig().REGISTRATION.showsStateSelect;

  return {
    addressLine1,
    city: address.city,
    postCode,
    ...(address.line2 ? { addressLine2: address.line2 } : {}),
    ...(showsStateSelect && address.state ? { state: address.state } : {}),
  };
}

export { applyAddressSchemaValidationErrors, buildProfileAddressDataPayload, createAddressEditFormSchema, type AddressEditFormData };
